import { z } from "zod";
import { authorityLevelEnum } from "./enums.js";

// --- Input schemas ---

export const discoveryRequestSchema = z.object({
  description: z.string().min(1).max(2000),
  language: z.string().max(10).optional(),
  authorityLevelFocus: z.array(authorityLevelEnum).optional(),
});

export type DiscoveryRequest = z.infer<typeof discoveryRequestSchema>;

// --- Query generation output ---

export const discoverySearchQuerySchema = z.object({
  query: z.string(),
  hierarchyTarget: z.string(),
});

export type DiscoverySearchQuery = z.infer<typeof discoverySearchQuerySchema>;

// --- Pre-flight types ---

export const pageClassificationEnum = z.enum([
  "content",
  "hub",
  "spa",
  "pdf",
  "unknown",
]);

export type PageClassification = z.infer<typeof pageClassificationEnum>;

export const preflightResultSchema = z.object({
  classification: pageClassificationEnum,
  wordCount: z.number().int().min(0).optional(),
  links: z
    .array(z.object({ text: z.string(), url: z.string().url() }))
    .optional(),
  scopeDescription: z.string(),
});

export type PreflightResult = z.infer<typeof preflightResultSchema>;

export const hubAnalysisSchema = z.object({
  ingestStrategy: z.enum(["filtered_pages", "full_crawl"]),
  reason: z.string(),
  relevantLinks: z.array(
    z.object({
      url: z.string().url(),
      reason: z.string(),
      rank: z.number().int().min(1),
    }),
  ),
});

export type HubAnalysis = z.infer<typeof hubAnalysisSchema>;

// --- Knowledge area types ---

export const knowledgeAreaSourceSchema = z.object({
  candidateIndex: z.number().int(),
  scopeDescription: z.string(),
  role: z.enum(["primary", "supplementary"]),
  rank: z.number().int(),
});

export type KnowledgeAreaSource = z.infer<typeof knowledgeAreaSourceSchema>;

export const knowledgeAreaSchema = z.object({
  topic: z.string(),
  whyItMatters: z.string(),
  whatItUnlocks: z.string(),
  sources: z.array(knowledgeAreaSourceSchema),
  priority: z.enum(["critical", "high", "medium"]),
});

export type KnowledgeArea = z.infer<typeof knowledgeAreaSchema>;

// --- Candidate schemas ---

export const deduplicationStatusSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("none") }),
  z.object({
    type: z.literal("exact_url"),
    existingSourceId: z.string().uuid(),
    existingSourceName: z.string(),
  }),
  z.object({
    type: z.literal("similar_name"),
    existingSourceId: z.string().uuid(),
    existingSourceName: z.string(),
  }),
]);

export type DeduplicationStatus = z.infer<typeof deduplicationStatusSchema>;

export const discoveryCandidateSchema = z.object({
  url: z.string().url(),
  suggestedName: z.string(),
  suggestedAuthorityLevel: authorityLevelEnum,
  suggestedTags: z.array(z.string()),
  language: z.string(),
  relevanceExplanation: z.string(),
  relevanceScore: z.number().min(0).max(1),
  isPrimarySource: z.boolean(),
  hierarchyLevel: z.string(),
  isPdf: z.boolean(),
  deduplication: deduplicationStatusSchema,
  referencedBy: z.string().optional(),
  needsPreflight: z.boolean().optional(),
  preflight: preflightResultSchema.optional(),
  subPages: z
    .array(
      z.object({
        url: z.string().url(),
        reason: z.string(),
        rank: z.number().int().min(1),
      }),
    )
    .optional(),
  ingestStrategy: z.enum(["filtered_pages", "full_crawl"]).optional(),
});

export type DiscoveryCandidate = z.infer<typeof discoveryCandidateSchema>;

// --- Streaming event schemas ---

export const discoveryPhaseEnum = z.enum([
  "generating_queries",
  "searching",
  "evaluating",
  "pre_flighting",
  "grouping",
  "done",
]);

export type DiscoveryPhase = z.infer<typeof discoveryPhaseEnum>;

export const discoveryEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("phase_change"),
    phase: discoveryPhaseEnum,
  }),
  z.object({
    type: z.literal("queries_generated"),
    queries: z.array(discoverySearchQuerySchema),
  }),
  z.object({
    type: z.literal("candidate"),
    candidate: discoveryCandidateSchema,
  }),
  z.object({
    type: z.literal("knowledge_areas"),
    areas: z.array(knowledgeAreaSchema),
  }),
  z.object({
    type: z.literal("knowledge_area"),
    area: knowledgeAreaSchema,
  }),
  z.object({
    type: z.literal("error"),
    message: z.string(),
  }),
  z.object({
    type: z.literal("done"),
    totalCandidates: z.number(),
  }),
]);

export type DiscoveryEvent = z.infer<typeof discoveryEventSchema>;

// --- Bulk ingest schemas ---

export const bulkIngestItemSchema = z.object({
  url: z.string().url(),
  name: z.string().min(1).max(500),
  authorityLevel: authorityLevelEnum,
  tags: z.array(z.string()),
  language: z.string().optional(),
  crawlDepth: z.number().int().min(0).max(5).default(0),
});

export type BulkIngestItem = z.infer<typeof bulkIngestItemSchema>;

export const bulkIngestRequestSchema = z.object({
  items: z.array(bulkIngestItemSchema).min(1).max(50),
});

export type BulkIngestRequest = z.infer<typeof bulkIngestRequestSchema>;

export const bulkIngestResultSchema = z.object({
  sourceId: z.string().uuid(),
  sourceVersionId: z.string().uuid(),
  jobId: z.string(),
  url: z.string().url(),
  name: z.string(),
});

export type BulkIngestResult = z.infer<typeof bulkIngestResultSchema>;

// --- Cross-reference expansion ---

export const crossReferenceRequestSchema = z.object({
  candidateUrls: z.array(z.string().url()).min(1).max(10),
});

export type CrossReferenceRequest = z.infer<typeof crossReferenceRequestSchema>;

// --- Discovery session schemas ---

export const discoverySessionStatusEnum = z.enum(["active", "completed"]);

export type DiscoverySessionStatus = z.infer<typeof discoverySessionStatusEnum>;

export const discoverySessionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  status: discoverySessionStatusEnum,
  description: z.string(),
  language: z.string().max(10).optional(),
  authorityFocus: z.array(authorityLevelEnum).optional(),
  candidates: z.array(discoveryCandidateSchema),
  sourceIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DiscoverySession = z.infer<typeof discoverySessionSchema>;

// --- SSE event schemas ---

export const ingestionStageEnum = z.enum([
  "queued",
  "crawling",
  "parsing",
  "chunking",
  "contextualizing",
  "embedding",
  "extracting",
  "ready",
  "error",
]);

export type IngestionStage = z.infer<typeof ingestionStageEnum>;

export const ingestionStatusEventSchema = z.object({
  type: z.literal("ingestion_status"),
  sourceId: z.string().uuid(),
  jobId: z.string(),
  stage: ingestionStageEnum,
  progress: z.number().min(0).max(100),
  error: z.string().optional(),
  /** Current page URL being crawled (crawler service progress). */
  currentUrl: z.string().optional(),
  /** Current page title (crawler service progress). */
  currentTitle: z.string().optional(),
  /** Total pages processed so far. */
  totalProcessed: z.number().optional(),
  /** Total chunks created so far. */
  chunksCreated: z.number().optional(),
  /** Total links discovered so far. */
  linksFound: z.number().optional(),
});

export type IngestionStatusEvent = z.infer<typeof ingestionStatusEventSchema>;

export const sessionSSEEventSchema = z.discriminatedUnion("type", [
  // Discovery events (reuse existing types)
  z.object({
    type: z.literal("phase_change"),
    phase: discoveryPhaseEnum,
  }),
  z.object({
    type: z.literal("queries_generated"),
    queries: z.array(discoverySearchQuerySchema),
  }),
  z.object({
    type: z.literal("candidate"),
    candidate: discoveryCandidateSchema,
  }),
  z.object({
    type: z.literal("knowledge_areas"),
    areas: z.array(knowledgeAreaSchema),
  }),
  z.object({
    type: z.literal("knowledge_area"),
    area: knowledgeAreaSchema,
  }),
  z.object({
    type: z.literal("error"),
    message: z.string(),
  }),
  z.object({
    type: z.literal("done"),
    totalCandidates: z.number(),
  }),
  // Ingestion status events
  ingestionStatusEventSchema,
  // Hydration event (sent on SSE reconnect)
  z.object({
    type: z.literal("hydration"),
    sources: z.array(
      z.object({
        sourceId: z.string().uuid(),
        jobId: z.string(),
        stage: ingestionStageEnum,
        progress: z.number().min(0).max(100),
        error: z.string().optional(),
      }),
    ),
  }),
]);

export type SessionSSEEvent = z.infer<typeof sessionSSEEventSchema>;
